<template>
    <!-- QR code dialog -->
    <v-dialog v-model="dialog" persistent scrollable>
        <!-- qr code scanner -->
        <v-card tile v-if="!growthTaskNodeId">
            <v-toolbar flat dark color="primary">
                <v-toolbar-title>Scan QR Code</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="pa-0 justify-center align-center grey">
                <qrcode-stream
                    v-if="!destroyed"
                    :camera="camera"
                    :torch="torchActive"
                    :track="paintOutline"
                    @decode="onDecode"
                    @init="onInit"
                >
                    <!-- loading progress bar -->
                    <v-card
                        color="transparent"
                        flat
                        class="mx-auto text-center"
                        fill-height
                        width="200px"
                        height="100%"
                        v-if="loading"
                    >
                        <v-responsive height="42%"></v-responsive>
                        <div class="grey--text text--darken-2">
                            Loading...
                        </div>
                        <v-row class="justify-center">
                            <v-col cols="12">
                                <v-progress-linear
                                    color="primary accent-4"
                                    indeterminate
                                    rounded
                                    height="5"
                                ></v-progress-linear>
                            </v-col>
                        </v-row>
                    </v-card>
                    <v-btn
                        icon
                        outlined
                        dark
                        v-if="!torchNotSupported"
                        class="white--text ml-2 mt-2"
                        @click="torchActive = !torchActive"
                        :disabled="!torchNotSupported"
                    >
                        <v-icon v-if="!torchActive">flash_on</v-icon>
                        <v-icon v-else>flash_off</v-icon>
                    </v-btn>

                    <!-- <v-btn
                        icon
                        outlined
                        dark
                        class="white--text ml-2 mt-2"
                        @click="switchCamera"
                    >
                        <v-icon>flip_camera_android</v-icon>
                    </v-btn> -->

                    <div
                        v-show="showScanConfirmation"
                        class="scan-confirmation"
                    >
                        <v-icon color="success">done</v-icon>
                    </div>
                </qrcode-stream>
            </v-card-text>
            <v-alert
                v-if="error"
                :value="true"
                type="error"
                class="ma-0"
                tile
                dark
                dismissible
                @dismissed="error = false">
                {{ error }}
            </v-alert>
        </v-card>

        <!-- growth task -->
        <v-card tile v-if="!loading && growthTaskNodeId">
            <v-toolbar flat dark color="primary">
                <v-toolbar-title>Growth Task</v-toolbar-title>
                <!-- <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            small
                            fab
                            class="ml-5"
                            @click="rescan"
                            v-on="on"
                        >
                            <v-icon>qr_code_scanner</v-icon>
                        </v-btn>
                    </template>
                    <span></span>
                </v-tooltip> -->

                <v-btn icon small fab class="ml-5" @click="rescan">
                    <v-icon>qr_code_scanner</v-icon>
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn
                    icon
                    dark
                    @click="goToGrowthTaskDetail(growthTaskNodeId)"
                >
                    <v-icon>launch</v-icon>
                </v-btn>
                <v-btn icon dark @click="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="mx-0 px-0">
                <GrowthTaskDetailComponent
                    v-if="growthTaskNodeId"
                    :growthTaskNodeId="growthTaskNodeId"
                    v-on:unknown="onUnknownGrowthTaskNodeId"
                ></GrowthTaskDetailComponent>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import GrowthTaskDetailComponent from "@/components/growth/GrowthTaskDetailComponent";
import { QrcodeStream } from "vue-qrcode-reader";

export default {
    name: "qr-code-scanner",
    props: ["dialog"],
    components: {
        GrowthTaskDetailComponent,
        QrcodeStream
    },

    data: function() {
        return {
            camera: "auto",
            torchActive: false,
            torchNotSupported: false,
            noRearCamera: false,
            noFrontCamera: false,
            loading: false,
            destroyed: false,
            showScanConfirmation: false,
            result: "",
            error: "",

            growthTaskNodeId: null
        };
    },

    computed: {
        baseUrl() {
            return location.origin;
        },
        growthTaskFullUrl() {
            return this.baseUrl + "/#/growthTask/" + this.task.id;
        }
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.destroyed = false;
            } else {
                this.destroyed = true;
            }
        }
    },
    methods: {
        async onInit(promise) {
            this.loading = true;
            try {
                const { capabilities } = await promise;

                // console.log("capabilities:", capabilities);

                this.torchNotSupported = !capabilities.torch;
            } catch (error) {
                if (error.name === "NotAllowedError") {
                    this.error =
                        "ERROR: you need to grant camera access permission";
                } else if (error.name === "NotFoundError") {
                    this.error = "ERROR: no camera on this device";
                } else if (error.name === "NotSupportedError") {
                    this.error =
                        "ERROR: secure context required (HTTPS, localhost)";
                } else if (error.name === "NotReadableError") {
                    this.error = "ERROR: is the camera already in use?";
                } else if (error.name === "OverconstrainedError") {
                    this.error = "ERROR: installed cameras are not suitable";
                } else if (error.name === "StreamApiNotSupportedError") {
                    this.error =
                        "ERROR: Stream API is not supported in this browser";
                } else if (error.name === "InsecureContextError") {
                    this.error =
                        "ERROR: Camera access is only permitted in secure context. Use HTTPS or localhost rather than HTTP.";
                } else {
                    this.error = `ERROR: Camera error (${error.name})`;
                }

                const triedFrontCamera = this.camera === "front";
                const triedRearCamera = this.camera === "rear";

                const cameraMissingError =
                    error.name === "OverconstrainedError";

                if (triedRearCamera && cameraMissingError) {
                    this.noRearCamera = true;
                }

                if (triedFrontCamera && cameraMissingError) {
                    this.noFrontCamera = true;
                }

                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async onDecode(result) {
            this.result = result;

            try {
                const strs = result.split(":");

                if (strs[0] === "growthtask") {
                    this.growthTaskNodeId = strs[1].trim();
                    this.destroyed = true;
                } else {
                    this.error = "ERROR: invalid QR code. (" + result + ")";
                }
            } catch (e) {
                console.log(e);
            }

            // this.pause();
            // await this.timeout(500);
            // this.unpause();
        },
        paintOutline(detectedCodes, ctx) {
            for (const detectedCode of detectedCodes) {
                const [firstPoint, ...otherPoints] = detectedCode.cornerPoints;

                ctx.strokeStyle = "red";

                ctx.beginPath();
                ctx.moveTo(firstPoint.x, firstPoint.y);
                for (const { x, y } of otherPoints) {
                    ctx.lineTo(x, y);
                }
                ctx.lineTo(firstPoint.x, firstPoint.y);
                ctx.closePath();
                ctx.stroke();
            }
        },
        switchCamera() {
            switch (this.camera) {
                case "front":
                    this.camera = "rear";
                    break;
                case "rear":
                    this.camera = "front";
                    break;
            }
        },
        rescan() {
            this.result = "";
            this.error = "";
            this.growthTaskNodeId = null;
            this.destroyed = false;
        },
        closeDialog() {
            this.result = "";
            this.error = "";
            this.growthTaskNodeId = null;
            this.$emit("update:dialog", false);
        },

        unpause() {
            this.camera = "auto";
        },

        pause() {
            this.camera = "off";
        },

        timeout(ms) {
            return new Promise(resolve => {
                window.setTimeout(resolve, ms);
            });
        },
        onUnknownGrowthTaskNodeId() {
            this.growthTaskNodeId = null;
            this.destroyed = false;
        },

        goToGrowthTaskDetail(growthTaskNodeId) {
            let routeData = this.$router.resolve({
                name: "growthtask-detail",
                params: { id: growthTaskNodeId }
            });
            window.open(routeData.href, "_blank");
        }
    }
};
</script>


<style scoped>
.box-border {
    border-style: solid;
    padding: 30px;
}

.loading-indicator {
    font-weight: bold;
    font-size: 1rem;
    text-align: center;
}

.scan-confirmation {
    position: absolute;
    width: 100%;
    height: 100%;

    background-color: rgba(255, 255, 255, 0.8);

    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
}
</style>
